import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import {
  AppState,
  CategoryFilterOption,
  SubcategoryFilterState,
  SubcategoryOption,
  SubcategoryOptionsMap,
} from '@immosky/types';
import { FocusKeyManager, FocusOrigin } from '@angular/cdk/a11y';
import {
  cloneSubcategoryFilter,
  selectSubcategoryFilter,
} from '@immosky/object-search-filter';
import { CheckboxComponent } from '../checkbox/checkbox.component';

const VISIBLE_COUNT_ALL = -1;

@Component({
  selector: 'lib-subcategory-filter',
  templateUrl: './subcategory-filter.component.html',
  styleUrls: ['./subcategory-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubcategoryFilterComponent implements AfterViewInit {
  private subcategoriesSubscription: Subscription;

  tabIndex = 0;
  focusKeyManager!: FocusKeyManager<CheckboxComponent>;

  currentFilter!: SubcategoryOptionsMap;
  filterState!: SubcategoryFilterState;
  isExpanded = false;
  hasFocus = false;

  @Input()
  categoryFilter!: CategoryFilterOption;

  @Input()
  visibleCount = VISIBLE_COUNT_ALL;

  @Output()
  update = new EventEmitter<SubcategoryOptionsMap>();

  @ViewChildren(CheckboxComponent)
  checkboxes!: QueryList<CheckboxComponent>;

  get canExpand(): boolean {
    return (
      this.visibleCount !== VISIBLE_COUNT_ALL &&
      this.options.length > this.visibleCount
    );
  }

  get options(): SubcategoryOption[] {
    return this.currentFilter?.[this.categoryFilter?.value] || [];
  }

  get visibleOptions(): SubcategoryOption[] {
    if (this.canExpand && !this.isExpanded) {
      return this.options.slice(0, this.visibleCount);
    }
    return this.options;
  }

  get hasSubcategoryOptions(): boolean {
    return this.options.length > 0;
  }

  get canReset(): boolean {
    return this.getIndexOfFirstSelectedOption() > -1;
  }

  constructor(
    private cdRef: ChangeDetectorRef,
    private store: Store<AppState>
  ) {
    this.subcategoriesSubscription = this.store
      .pipe(select(selectSubcategoryFilter))
      .subscribe((filterState: SubcategoryFilterState) => {
        this.filterState = filterState;
        this.currentFilter = cloneSubcategoryFilter(filterState.filter);
        this.cdRef.markForCheck();
      });
  }

  //
  // ngOnChanges(changes: SimpleChanges): void {
  //   if (changes.categoryFilter?.currentValue?.value) {
  //     this.options = this.currentFilter[this.categoryFilter.value];
  //   }
  // }

  ngAfterViewInit(): void {
    this.focusKeyManager = new FocusKeyManager<CheckboxComponent>(
      this.checkboxes
    )
      .withWrap()
      .withHomeAndEnd()
      .withVerticalOrientation()
      .withHorizontalOrientation('ltr');
  }

  resetSubcategories() {
    this.options.forEach((option) => (option.isChecked = false));
    this.emitChange();
  }

  toggleSubcategory(
    option: SubcategoryOption,
    isChecked: boolean,
    index: number
  ) {
    option.isChecked = isChecked;
    this.focusKeyManager.setActiveItem(index);
    this.emitChange();
  }

  private emitChange() {
    this.update.emit(this.currentFilter);
  }

  onFocusChange(event: FocusOrigin) {
    if (event) {
      if (!this.hasFocus && event !== 'mouse') {
        this.onFocus();
      }
      this.hasFocus = true;
      this.tabIndex = -1;
    } else {
      this.hasFocus = false;
      this.tabIndex = 0;
    }
    this.cdRef.markForCheck();
  }

  onKeydown(event: KeyboardEvent) {
    this.focusKeyManager.onKeydown(event);
  }

  onFocus() {
    const safeIndex = Math.max(0, this.getIndexOfFirstSelectedOption());
    this.focusKeyManager.setActiveItem(safeIndex);
  }

  private getIndexOfFirstSelectedOption(): number {
    return this.options.findIndex((option) => {
      return option.isChecked;
    });
  }

  toggleExpanded() {
    this.isExpanded = !this.isExpanded;
  }

  trackBy(index: number, option: SubcategoryOption) {
    return option.value;
  }
}
